import React, { useMemo, useState } from 'react';
import { TableCell, TableRow } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { FinancialConditionTableCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableCell.tsx';
import { FinancialConditionTableDeleteCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableDeleteCell.tsx';
import { normalizeDecimalValue } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import {
  findKvedByOption,
  mapKvedsToAutocompleteOptions,
  useKvedPageableQuery,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/useKvedPageableQuery.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

interface IIncomeStructureByKvedIncomeRowProps {
  index: number;
  canRemove: boolean;
  disabled: boolean;
  onRemove: () => void;
}

export const IncomeStructureByKvedIncomeRow: React.FC<IIncomeStructureByKvedIncomeRowProps> = ({
  index,
  canRemove,
  disabled,
  onRemove,
}) => {
  const { clearErrors, setError, setValue } = useFormContext<TIncomeStructureByKvedFormValues>();
  const [search, setSearch] = useState('');
  const { kveds, isLoading } = useKvedPageableQuery(search);
  const kvedOptions = useMemo<TAutocompleteOption[]>(
    () => mapKvedsToAutocompleteOptions(kveds),
    [kveds]
  );
  const kvedCodeFieldName = `incomes.${index}.kvedCode` as const;
  const economicActivityGroupFieldName = `incomes.${index}.economicActivityGroup` as const;

  const handleKvedChange = (value: TAutocompleteOption | TAutocompleteOption[] | null) => {
    if (Array.isArray(value)) {
      return;
    }

    if (!value) {
      setSearch('');
      setValue(kvedCodeFieldName, null, { shouldDirty: true, shouldTouch: true });
      setValue(economicActivityGroupFieldName, '', { shouldDirty: true, shouldTouch: true });
      clearErrors(kvedCodeFieldName);
      return;
    }

    const kved = findKvedByOption(kveds, value);

    if (!kved) {
      setValue(kvedCodeFieldName, null, { shouldDirty: true, shouldTouch: true });
      setValue(economicActivityGroupFieldName, '', { shouldDirty: true, shouldTouch: true });
      setError(kvedCodeFieldName, { type: 'validate', message: 'КВЕД не знайдено' });
      return;
    }

    setValue(
      kvedCodeFieldName,
      { label: kved.code, value: kved.code },
      { shouldDirty: true, shouldTouch: true }
    );
    setValue(economicActivityGroupFieldName, kved.name, {
      shouldDirty: true,
      shouldTouch: true,
    });
    clearErrors(kvedCodeFieldName);
  };

  const handleKvedSearchInput = (event: React.FormEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  return (
    <TableRow>
      <TableCell align="center">{index + 1}</TableCell>
      <FinancialConditionTableCell>
        <FormAutocomplete
          name={kvedCodeFieldName}
          options={kvedOptions}
          filterOptions={(options) => options}
          label="Код за КВЕД 2010"
          disabled={disabled}
          fullWidth
          loading={isLoading}
          inputProps={{ inputMode: 'numeric', onInput: handleKvedSearchInput }}
          onChange={handleKvedChange}
          sx={documentGenerationFormStyles.field}
        />
      </FinancialConditionTableCell>
      <FinancialConditionTableCell>
        <FormInput
          name={economicActivityGroupFieldName}
          label="Групи видів економічної діяльності"
          disabled={disabled}
          fullWidth
          sx={documentGenerationFormStyles.field}
        />
      </FinancialConditionTableCell>
      <FinancialConditionTableCell>
        <FormInput
          name={`incomes.${index}.incomeAmount`}
          label="Сума доходу, тис. грн."
          disabled={disabled}
          fullWidth
          type="text"
          inputProps={{ inputMode: 'decimal' }}
          normalizeOnBlur={normalizeDecimalValue}
          sx={documentGenerationFormStyles.field}
        />
      </FinancialConditionTableCell>
      <FinancialConditionTableDeleteCell
        label={`Видалити дохід ${index + 1}`}
        disabled={disabled}
        hidden={!canRemove}
        onDelete={onRemove}
      />
    </TableRow>
  );
};
